/**
 * LO QUE SE PUEDE ADJUNTAR DESDE UN CLAIM, Y CÓMO SE ORDENA (dueño 2026-10-28).
 *
 * Reporte de Gabriela: no aparecían los invoices y el selector abría diminuto. Pedido del dueño:
 * "una pantalla en el medio, un poco más grande, donde puedas ver qué es, qué tipo de files, organizados
 * por estimados, invoices, contratistas, lo que sea, bien organizado, y sus montos a la derecha; y cuándo
 * se subió y quién. Eso es para todos los emails". Esta lib es la parte PURA: el tipo de fila que
 * devuelve `/api/claims/:id/files` (lo que el selector necesita de ella) y el grupo de cada archivo.
 * Los tres composes (claim, comms, bandeja) la comparten para que la ventana sea la misma.
 */
export type AttachableFile = {
  id: string;
  file_name: string | null;
  doc_type: string | null;
  doc_label?: string | null;
  file_key?: string | null;
  created_at?: string | null;
  uploaded_by?: string | null;
  origin_kind?: string | null;
  origin_detail?: string | null;
  source_tag?: string | null;
  amount_cents?: number | null;
  financials?: { is_ours?: boolean; rcv_amount_cents?: number | null } | null;
  supplement_status?: { kind: 'resolved' | 'pre_approved'; at: string; by: string | null } | null;
  invoice?: { id: string; number: string | null; status: string | null; issued_at: string | null; total_cents: number | null } | null;
  contractor_invoice_of?: string | null;
  external_url?: string | null;
};

/** CUALQUIER archivo del claim con nombre es adjuntable (reporte de Brenda: antes solo PDF/ESX). */
export const esAdjuntable = (f: { file_name: string | null }) => Boolean((f.file_name ?? '').trim());

/** El monto que se pinta a la derecha: el RCV del estimado, o el total leído / del invoice. */
export const montoDeAdjunto = (f: AttachableFile): number | null => f.financials?.rcv_amount_cents ?? f.amount_cents ?? null;

export type GrupoAdjunto = 'proactive' | 'insurance' | 'invoices' | 'contracts' | 'checks' | 'coc' | 'policy' | 'photos' | 'atr' | 'other';

/** En este orden salen los grupos en la ventana: primero lo que más se manda. */
export const GRUPOS_ADJUNTO: GrupoAdjunto[] = ['proactive', 'insurance', 'invoices', 'contracts', 'checks', 'coc', 'policy', 'photos', 'atr', 'other'];

const OURS = ['proactive_estimate', 'mitigation_estimate', 'rebuild_estimate'];

export function grupoDeAdjunto(f: AttachableFile): GrupoAdjunto {
  const dt = (f.doc_type ?? '').toLowerCase();
  const name = (f.file_name ?? '').toLowerCase();
  const label = (f.doc_label ?? '').toLowerCase();
  if (f.invoice || dt.includes('invoice') || /\binvoice\b/.test(name)) return 'invoices';
  if (OURS.includes(dt) || f.financials?.is_ours) return 'proactive';
  if (dt === 'estimate' || dt === 'insurance_estimate') return 'insurance';
  if (dt === 'estimate_esx' || name.endsWith('.esx')) return f.financials?.is_ours === false ? 'insurance' : 'proactive';
  if (dt === 'coc') return 'coc';
  if (dt === 'policy') return 'policy';
  if (dt === 'atr_video' || dt === 'recording') return 'atr';
  if (/check|payment|receipt/.test(dt) || /\bcheck\b|receipt/.test(name)) return 'checks';
  if (/contract|signed_contract/.test(dt) || /contract|\blor\b|letter of rep/.test(`${label} ${name}`)) return 'contracts';
  if (dt === 'job_photo' || /\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(name)) return 'photos';
  return 'other';
}

/** Los archivos agrupados, cada grupo con los más nuevos arriba; los grupos vacíos no salen. */
export function agruparAdjuntos(files: AttachableFile[]): Array<{ grupo: GrupoAdjunto; files: AttachableFile[] }> {
  const por = new Map<GrupoAdjunto, AttachableFile[]>();
  for (const f of files) {
    const g = grupoDeAdjunto(f);
    const arr = por.get(g) ?? [];
    arr.push(f);
    por.set(g, arr);
  }
  const fecha = (f: AttachableFile) => (f.created_at ? new Date(f.created_at).getTime() : 0);
  return GRUPOS_ADJUNTO
    .filter((g) => (por.get(g)?.length ?? 0) > 0)
    .map((g) => ({ grupo: g, files: [...por.get(g)!].sort((a, b) => fecha(b) - fecha(a)) }));
}

/** El filtro del buscador: nombre, etiqueta, número de invoice, contratista, quién lo subió. */
export function coincideAdjunto(f: AttachableFile, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const campos = [f.file_name, f.doc_label, f.doc_type, f.invoice?.number, f.contractor_invoice_of, f.uploaded_by, f.origin_detail, f.source_tag];
  return campos.some((v) => (v ?? '').toLowerCase().includes(s));
}
